import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { airSavedCardStore } from "entities/AirSavedCard";
import { airSearchCardStore, createAirSearchQueryParams } from "entities/AirSearchCard";
import { authStore } from "entities/Auth";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";

import { airSearchValueValidator } from "../utils";
import { useAirSearch } from "./airSearch.hook";

type UseAirRequestStrategyReturnType = {
  handleSearch: (reference: string) => void;
};

type UseAirRequestStrategyParams = {
  isReferenceEqualToOpenedCard: (reference: string) => boolean;
  searchValue: string;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  openCard: (card: ShipmentModel) => void;
  showMainView: () => void;
  showIntroView: () => void;
  clearViewHistory: () => void;
};

export const useAirRequestStrategy = ({
  isReferenceEqualToOpenedCard,
  searchValue,
  setErrorMessage,
  openCard,
  showMainView,
  showIntroView,
  clearViewHistory,
}: UseAirRequestStrategyParams): UseAirRequestStrategyReturnType => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const savedCards = useAppSelector(airSavedCardStore.selectors.savedCards);
  const searchCards = useAppSelector(airSearchCardStore.selectors.searchCards);
  const { airSearchRequest, getAirCards, isAirSearchLoading } = useAirSearch({ isAuthenticated });

  const validatorMeta = useRef<ReturnType<typeof airSearchValueValidator.validate>>(
    airSearchValueValidator.validate(searchValue)
  );

  useEffect(() => {
    validatorMeta.current = airSearchValueValidator.validate(searchValue);
  }, [searchValue]);

  const getRequestFuncByStrategy = (
    strategy: "authenticated" | "not-authenticated",
    reference: string
  ): (() => void) | null => {
    const reqParams = createAirSearchQueryParams({
      requestType: "search",
      model: { reference },
    });
    if (!reqParams) return null;

    switch (strategy) {
      case "authenticated":
        return () => {
          getAirCards(
            { where: { reference } },
            {
              onSettled: (response) => {
                if (response?.length) {
                  const data = response[0];
                  dispatch(airSavedCardStore.actions.prependSavedCardAction({ data }));
                  if (data?.reference) {
                    openCard(data);
                  }
                } else {
                  airSearchRequest(reqParams, {
                    onSuccess: (response) => {
                      if (response.requestLimitReached) return;

                      dispatch(airSearchCardStore.actions.prependSearchCardAction({ data: response }));
                      if (response?.reference) {
                        if (response.isSuccess) {
                          openCard(response);
                        } else {
                          showMainView();
                        }
                      }
                    },
                  });
                }
              },
            }
          );
        };
      case "not-authenticated":
      default:
        return () => {
          airSearchRequest(reqParams, {
            onWebAppRequestLimitReached: () => {
              if (searchCards.length > 0) {
                showMainView();
              } else {
                showIntroView();
              }
            },
            onSuccess: (response) => {
              if (response.requestLimitReached) return;

              dispatch(airSearchCardStore.actions.prependSearchCardAction({ data: response }));
              if (response?.reference) {
                if (response.isSuccess) {
                  openCard(response);
                } else {
                  showMainView();
                }
              }
            },
          });
        };
    }
  };

  /**
   * 1. find existing search card OR
   * 2. find existing user card OR
   * 3. search user card in DB OR
   * 4. run search and prepend search card to search cards list
   */
  const performRequest = async (reference: string) => {
    const targetSearchCard = searchCards.find((card) => card.reference === reference);
    if (targetSearchCard) {
      if (targetSearchCard?.reference && targetSearchCard.isSuccess) {
        openCard(targetSearchCard);
      } else {
        showMainView();
      }
      return;
    }

    const targetSavedCard = savedCards.find((card) => card.reference === reference);
    if (targetSavedCard) {
      if (targetSavedCard?.reference) {
        openCard(targetSavedCard);
      }
      return;
    }

    if (isAirSearchLoading) return;

    const requestFunc = getRequestFuncByStrategy(isAuthenticated ? "authenticated" : "not-authenticated", reference);

    requestFunc?.();
  };

  const handleSearch = (reference: string) => {
    const { isValid, errorMessage } = validatorMeta.current;

    if (isValid) {
      if (isReferenceEqualToOpenedCard(reference)) return;
      setErrorMessage("");
      performRequest(reference);
      clearViewHistory();
      return;
    }
    setErrorMessage(errorMessage);
  };

  return {
    handleSearch,
  };
};
